import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";

// Apply persisted theme from slash-command settings (once at module load)
try {
  const t = localStorage.getItem("alfred_theme");
  if (t === "dark") document.documentElement.classList.add("dark");
  else if (t === "light") document.documentElement.classList.remove("dark");
  else if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
    document.documentElement.classList.add("dark");
  }
} catch {}
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import QuickstartPage from "@/pages/QuickstartPage";
import AuthCallback from "@/pages/AuthCallback";
import AppShell from "@/pages/AppShell";
import Dashboard from "@/pages/Dashboard";
import JobsList from "@/pages/JobsList";
import JobDetail from "@/pages/JobDetail";
import JobDescriptionsPage from "@/pages/JobDescriptionsPage";
import JobSettingsPage from "@/pages/JobSettingsPage";
import AssessmentsPage from "@/pages/AssessmentsPage";
import CandidateDetail from "@/pages/CandidateDetail";
import TalentSearch from "@/pages/TalentSearch";
import People from "@/pages/People";
import Onboarding from "@/pages/Onboarding";
import OnboardingTemplatesPage from "@/pages/OnboardingTemplatesPage";
import Compliance from "@/pages/Compliance";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import NotificationsPage from "@/pages/NotificationsPage";
import ContentLibraryPage from "@/pages/ContentLibraryPage";
import TalentPoolsPage from "@/pages/TalentPoolsPage";
import HRHub from "@/pages/HRHub";
import OffboardingPage from "@/pages/OffboardingPage";
import CompliancePlusPage from "@/pages/CompliancePlusPage";
import CandidatePortal from "@/pages/CandidatePortal";
import InterviewsPage from "@/pages/InterviewsPage";
import CalendarPage from "@/pages/CalendarPage";
import MeetingRoom from "@/pages/MeetingRoom";
import ReferenceFormPage from "@/pages/ReferenceFormPage";
import BehavioralFormPage from "@/pages/BehavioralFormPage";
import MeShell from "@/pages/me/MeShell";
import MeHome from "@/pages/me/MeHome";
import MeDocuments from "@/pages/me/MeDocuments";
import MeLeave from "@/pages/me/MeLeave";
import MeCompliance from "@/pages/me/MeCompliance";
import MeProfile from "@/pages/me/MeProfile";
import MeTimesheet from "@/pages/me/MeTimesheet";
import MeReviews from "@/pages/me/MeReviews";
import MeLearning from "@/pages/me/MeLearning";
import MeSafety from "@/pages/me/MeSafety";
import CandidateHome from "@/pages/CandidateHome";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-neutral-600" data-testid="app-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Route candidates to their own home; keep everyone else where they were headed
  const path = window.location.pathname;
  if (user.role === "CANDIDATE" && !path.startsWith("/candidate") && !path.startsWith("/reference") && !path.startsWith("/behavioral") && !path.startsWith("/interview") && !path.startsWith("/meeting")) {
    return <Navigate to="/candidate" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback synchronously
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/quickstart" element={<QuickstartPage />} />
      <Route path="/docs" element={<QuickstartPage />} />
      <Route path="/interview/:inviteId" element={<CandidatePortal />} />
      <Route path="/meeting/:token" element={<MeetingRoom />} />
      <Route path="/reference/:token" element={<ReferenceFormPage />} />
      <Route path="/behavioral/:token" element={<BehavioralFormPage />} />
      <Route path="/candidate" element={<Protected><CandidateHome /></Protected>} />

      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="recruit/jobs" element={<JobsList />} />
        <Route path="recruit/jobs/:jobId" element={<JobDetail />} />
        <Route path="recruit/descriptions" element={<JobDescriptionsPage />} />
        <Route path="recruit/assessments" element={<AssessmentsPage />} />
        <Route path="recruit/settings" element={<JobSettingsPage />} />
        <Route path="recruit/candidates/:candidateId" element={<CandidateDetail />} />
        <Route path="recruit/talent" element={<TalentSearch />} />
        <Route path="recruit/library" element={<ContentLibraryPage />} />
        <Route path="recruit/pools" element={<TalentPoolsPage />} />
        <Route path="interviews" element={<InterviewsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="people" element={<People />} />
        <Route path="hr" element={<HRHub />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="onboarding/templates" element={<OnboardingTemplatesPage />} />
        <Route path="offboarding" element={<OffboardingPage />} />
        <Route path="compliance" element={<Compliance />} />
        <Route path="compliance-plus" element={<CompliancePlusPage />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="settings" element={<Settings />} />
        <Route path="notifications" element={<NotificationsPage />} />
      </Route>

      <Route path="/me" element={<Protected><MeShell /></Protected>}>
        <Route index element={<MeHome />} />
        <Route path="documents" element={<MeDocuments />} />
        <Route path="leave" element={<MeLeave />} />
        <Route path="timesheet" element={<MeTimesheet />} />
        <Route path="reviews" element={<MeReviews />} />
        <Route path="learning" element={<MeLearning />} />
        <Route path="safety" element={<MeSafety />} />
        <Route path="compliance" element={<MeCompliance />} />
        <Route path="profile" element={<MeProfile />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-center" />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
